<div class="lineup-container py-2">

  @if (!isDetailView) {
    <!-- Loading state -->
  @if (isLoading) {
    <div class="d-flex justify-content-center align-items-center py-5">
      <div class="spinner-border text-primary" role="status">
        <span class="visually-hidden">{{ 'LOADING' | translate }}</span>
      </div>
    </div>
  }

  <!-- Error state -->
  @if (hasError && !isLoading) {
    <div class="alert alert-danger mx-3">
      <h4>{{ 'ERROR' | translate }}</h4>
      <p>{{ errorMessage }}</p>
    </div>
  }

  <!-- Lineup content -->
  @if (!isLoading && !hasError) {
    <div>
      <!-- Brand + Family Filter Bar -->
      <!-- Spacer keeps the layout flow; inner div is fixed so it overlays while scrolling -->
      <div #filterBarRef [style.height.px]="filterBarHeight">
        <div class="bg-light pt-3 pb-2 border-bottom" [class.filter-bar-fixed]="navbarFixed" [class.mt-4]="isInternal">
          <div class="container">
            <div class="d-flex align-items-center gap-3 flex-column flex-sm-row flex-wrap">
            <!-- Brand dropdown -->
            @if (brandOptions.length > 1) {
              <div class="d-flex align-items-center gap-2">
                <label class="fw-semibold text-secondary mb-0" for="brandFilter">{{ 'BRAND' | translate }}:</label>
                <select
                  id="brandFilter"
                  name="brandFilter"
                  class="form-select w-auto"
                  style="min-width: 180px;"
                  [(ngModel)]="selectedBrand"
                  (ngModelChange)="onBrandChange($event)">
                  @for (brand of brandOptions; track brand.name) {
                    <option [value]="brand.name">{{ brand.name }}</option>
                  }
                </select>
              </div>
            }
            <!-- Model year dropdown -->
            @if (modelYearOptions.length > 0) {
              <div class="d-flex align-items-center gap-2">
                <label class="fw-semibold text-secondary mb-0" for="modelYearFilter">{{ 'MODEL_YEAR' | translate }}:</label>
                <select
                  id="modelYearFilter"
                  name="modelYearFilter"
                  class="form-select w-auto"
                  style="min-width: 180px;"
                  [(ngModel)]="selectedModelYear"
                  (ngModelChange)="onModelYearChange()">
                  <option value="">{{ 'ALL' | translate }}</option>
                  @for (year of modelYearOptions; track year) {
                    <option [value]="year">{{ year }} </option>
                  }
                </select>
              </div>
            }
  
            <!-- Family dropdown -->
            <label class="fw-semibold text-secondary mb-0" for="familyFilter">{{ 'FILTER_BY_FAMILY' | translate }}:</label>
            <select
              id="familyFilter"
              name="familyFilter"
              class="form-select w-auto" style="min-width: 250px;"
              [(ngModel)]="selectedFamily"
              (change)="onFamilyChange($event)">
              <option value="">{{ 'ALL' | translate }}</option>
              @for (option of familyOptions.slice(1); track option) {
                <option [value]="option.value">{{ option.label }}</option>
              }
            </select>

            <!-- Download button -->
            <button class="btn btn-outline-secondary ms-auto" [disabled]="isDownloading" (click)="downloadLineup()">
              @if (isDownloading) {
                <span class="spinner-border spinner-border-sm me-1" role="status" aria-hidden="true"></span>
              } @else {
                <i class="pi pi-download me-1"></i>
              }
              {{ 'DOWNLOAD' | translate }}
            </button>
          </div>
        </div>
        </div>
      </div>

      <!-- Loop through families -->
      <div class="pt-5">
        @for (familyName of getFamilyNames(); track familyName; let i = $index) {
          <div [id]="getSectionId(familyName)" class="py-5 px-3 px-md-4 family-container" [class.bg-light]="i % 2 !== 0">
            <div class="container">
              <h2 class="h4 fw-bold text-uppercase mb-4 pb-1">
                {{ familyName }}
              </h2>
              <!-- Display models in grid -->
              <div class="models-grid" [@.disabled]="!isFirstVisit" [@pageAnimations]>
                @for (model of getModelsByFamily(familyName); track model.id) {
                  <a
                    [routerLink]="['/', companyGroupCode, 'dealerbook', getActiveVariant(model)?.modelId || model.id]"
                    class="model-container d-flex flex-column align-items-center text-center text-decoration-none text-dark model-card">
                    <div class="w-100 model-image-container mb-3 border rounded-3 bg-white">
                      @if (getActiveVariant(model)?.thumbnail || model.thumbnail) {
                        <img [src]="(getActiveVariant(model)?.thumbnail || model.thumbnail) + '?width=640'" [alt]="model.name"
                          class="w-100 h-100 p-2" style="object-fit: contain;" />
                      } @else {
                        <div class="w-100 h-100 d-flex align-items-center justify-content-center text-muted">
                          <span style="font-size: 2.5rem;">🚲</span>
                        </div>
                      }
                    </div>
                    <p class="small fw-semibold px-2 mb-1">{{ model.name }}</p>
                    <!-- Color variants -->
                    @if (model.variants?.length) {
                      <div class="d-flex align-items-center justify-content-center gap-2 mt-1"
                        (click)="$event.preventDefault()">
                        @for (variant of model.variants; track variant.name; let idx = $index) {
                          @if (variant.name?.toUpperCase() === 'MULTICOLOR') {
                            <div
                              [title]="variant.name"
                              (click)="selectVariant(model.uiKey, idx, $event)"
                              class="color-dot rounded-circle border border-2 d-flex align-items-center justify-content-center"
                              [class.selected]="isVariantSelected(model.uiKey, idx)"
                              [style.background]="'conic-gradient(red, yellow, lime, cyan, blue, magenta, red)'"
                              [style.borderColor]="isVariantSelected(model.uiKey, idx) ? '#007bff' : '#d1d5db'">
                              <span style="font-size: 0.55rem; font-weight: 700; color: #fff; text-shadow: 0 0 2px #000;">M</span>
                            </div>
                          } @else {
                            <div
                              [title]="variant.name"
                              (click)="selectVariant(model.uiKey, idx, $event)"
                              class="color-dot rounded-circle border border-2"
                              [class.selected]="isVariantSelected(model.uiKey, idx)"
                              [style.backgroundColor]="variant.color"
                              [style.borderColor]="isVariantSelected(model.uiKey, idx) ? '#007bff' : (variant.color === '#ffffff' ? '#d1d5db' : variant.color)">
                              @if (isVariantSelected(model.uiKey, idx)) {
                                <span class="checkmark">✓</span>
                              }
                            </div>
                          }
                        }
                      </div>
                    }
                  </a>
                }
              </div>
              <!-- View all in family button -->
              <div class="mt-4 text-center">
                <a [routerLink]="['/', companyGroupCode, 'search']"
                  [queryParams]="{ keyword: familyName, 'MODEL_YEARS': selectedModelYear, 'fromDealerbook': true }"
                  class="btn btn-outline-dark">
                  {{ 'VIEW_ALL' | translate }} {{ familyName }}
                </a>
              </div>
            </div>
          </div>
        }
      </div>
    </div>
  }
  } <!-- /!isDetailView -->

  <router-outlet></router-outlet>

</div>